import React, { Component, type SyntheticEvent } from 'react';

import { styled } from '@compiled/react';

import Button, { type Appearance } from '@atlaskit/button';
import AkInlineDialog, { type Placement } from '@atlaskit/inline-dialog';
import { token } from '@atlaskit/tokens';

import { type FireClickEvent } from '../../common/types/analytics';
import { buttonActionTypes } from '../../common/types/button';
import withEvents from '../../controllers/engagement-with-events';
import withMessage from '../../controllers/engagement-with-message';
import HtmlContent from '../../controllers/utils/html-content';

const baselineUnit = token('space.050', '4px');
const COLOR_N900 = '#091E42';

// eslint-disable-next-line @compiled/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const ContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @compiled/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const Image = styled.img({
	width: '100%',
});

// eslint-disable-next-line @compiled/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const ButtonContainer = styled.div({
	marginTop: baselineUnit,
});

// eslint-disable-next-line @compiled/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled
const BodyContainer = styled.div({
	paddingTop: baselineUnit,
	paddingBottom: baselineUnit,
	color: token('color.text', COLOR_N900),
});

export type Action = {
	type: string;
	// we only support links at this time
	data?: ActionData;
};

export type ActionData = {
	target: string;
	url: string;
};

type Props = {
	fireClickEvent: FireClickEvent;
	target: string;
	body: string;
	children: JSX.Element;
	imageAlt?: string;
	imageSrc?: string;
	order: string[];
	position: Placement;
	primaryAction?: Action;
	primaryText?: string;
	secondaryAction?: Action;
	secondaryText?: string;
	title: string;
	engagementId?: string;
};

type OnClick = (e: SyntheticEvent) => void;

// eslint-disable-next-line @repo/internal/react/no-class-components
class InlineDialog extends Component<Props> {
	static defaultProps: {
		children: null;
		order: string[];
		position: string;
	} = {
		children: null,
		order: ['title', 'image', 'body'],
		position: 'bottom left',
	};

	getPrimaryButton(): React.JSX.Element {
		const { primaryAction, primaryText } = this.props;

		return this.getActionButton('primary', this.onPrimaryClick, primaryAction, primaryText);
	}

	getSecondaryButton(): React.JSX.Element {
		const { secondaryAction, secondaryText } = this.props;

		return this.getActionButton(
			'subtle-link',
			this.onSecondaryClick,
			secondaryAction,
			secondaryText,
		);
	}

	onPrimaryClick = (): void => {
		this.props.fireClickEvent('primary');
	};

	onSecondaryClick = (): void => {
		this.props.fireClickEvent('secondary');
	};

	getActionButton(
		appearance: Appearance | undefined,
		onClick: OnClick,
		action?: Action,
		text?: string,
	): React.JSX.Element {
		const type = action ? action.type : buttonActionTypes.DISMISS;

		switch (type) {
			case buttonActionTypes.LINK: {
				const actionData: ActionData | undefined = action!.data;

				return (
					<Button
						href={actionData!.url}
						target={actionData!.target}
						appearance={appearance}
						onClick={onClick}
					>
						{text}
					</Button>
				);
			}
			default:
				return (
					<Button appearance={appearance} onClick={onClick}>
						{text}
					</Button>
				);
		}
	}

	render(): React.JSX.Element {
		const { children, title, imageSrc, imageAlt, body, position, order, secondaryText } =
			this.props;

		const messageContent: Record<string, JSX.Element | null> = {
			image: null,
			title: null,
			body: null,
		};

		if (title) {
			// eslint-disable-next-line @atlaskit/design-system/use-heading
			messageContent.title = <h4 key="title">{title}</h4>;
		}

		if (imageSrc) {
			messageContent.image = <Image key="img" src={imageSrc} alt={imageAlt} />;
		}

		messageContent.body = (
			<BodyContainer key="body">
				<HtmlContent html={body} />
			</BodyContainer>
		);

		const orderedContent = order.map((c) => messageContent[c]);

		const content = (
			<div>
				<ContentContainer>{orderedContent}</ContentContainer>

				<ButtonContainer>
					{this.getPrimaryButton()}

					{secondaryText ? this.getSecondaryButton() : null}
				</ButtonContainer>
			</div>
		);

		return (
			<AkInlineDialog content={content} placement={position} isOpen>
				{children}
			</AkInlineDialog>
		);
	}
}

const InlineDialogWithEvents: React.ComponentType<any> = withEvents(InlineDialog);

export { InlineDialog, InlineDialogWithEvents };

// eslint-disable-next-line camelcase
const _default_1: React.ComponentType<any> = withMessage(InlineDialogWithEvents, 'inlineDialog');
// eslint-disable-next-line camelcase
export default _default_1;
